"use client";

import { c as _c } from "react/compiler-runtime";
import { FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { ApiWaveDecisionPause } from "@/generated/models/ApiWaveDecisionPause";
import { useWave } from "@/hooks/useWave";
import { ApiWave } from "@/generated/models/ApiWave";
interface TimelineToggleHeaderProps {
  readonly isOpen: boolean;
  readonly setIsOpen: (isOpen: boolean) => void;
  readonly nextDecisionTime: number | null;
  readonly isPaused?: boolean;
  readonly currentPause?: ApiWaveDecisionPause | null;
  readonly wave?: ApiWave;
}

/**
 * Renders the header for the timeline with toggle functionality
 */
export const TimelineToggleHeader: FC<TimelineToggleHeaderProps> = t0 => {
  const $ = _c(26);
  const {
    isOpen,
    setIsOpen,
    nextDecisionTime,
    isPaused: t1,
    currentPause,
    wave
  } = t0;
  const isPaused = t1 === undefined ? false : t1;
  const waveData = useWave(wave);
  const hasNextDecision = !!nextDecisionTime;
  let t2;
  if ($[0] !== currentPause || $[1] !== isPaused || $[2] !== nextDecisionTime || $[3] !== wave || $[4] !== waveData) {
    t2 = () => {
      if (isPaused && currentPause) {
        return <span className="tw-inline-flex tw-items-center tw-gap-x-2 sm:tw-gap-x-2.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-bg-gradient-to-r tw-from-iron-800/90 tw-to-iron-700/70 group-hover:tw-from-iron-700/60 group-hover:tw-to-iron-600/40 tw-border tw-border-iron-600/40 group-hover:tw-border-iron-600/30 tw-border-solid tw-shadow-sm group-hover:tw-shadow-none tw-transition-all tw-duration-300 tw-ease-out"><span className="tw-font-bold tw-bg-gradient-to-r tw-from-amber-200 tw-via-amber-100 tw-to-amber-200/90 tw-bg-clip-text tw-text-transparent tw-whitespace-nowrap">Congrats to last SZN winners!</span><span className="tw-text-iron-500">•</span><span className="tw-text-iron-300 tw-font-medium tw-whitespace-nowrap">Next SZN starts:{" "}{(() => {
              const mintingDate = wave && waveData ? waveData.pauses.calculateMintingDate(nextDecisionTime) : null;
              return mintingDate ? new Date(mintingDate).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric"
              }) : new Date(currentPause.end_time).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric"
              });
            })()}</span></span>;
      }
      if (nextDecisionTime) {
        return <span className="tw-text-iron-300 tw-font-medium tw-text-xs">{new Date(nextDecisionTime).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          })}</span>;
      }
      return <span className="tw-text-iron-400 tw-text-xs">No upcoming events</span>;
    };
    $[0] = currentPause;
    $[1] = isPaused;
    $[2] = nextDecisionTime;
    $[3] = wave;
    $[4] = waveData;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const getStatusDisplay = t2;
  let t3;
  if ($[6] !== isOpen || $[7] !== setIsOpen) {
    t3 = () => setIsOpen(!isOpen);
    $[6] = isOpen;
    $[7] = setIsOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = `tw-text-xs tw-font-semibold tw-whitespace-nowrap tw-flex-shrink-0 ${hasNextDecision ? "tw-text-iron-100" : "tw-text-iron-400"}`;
  const t5 = hasNextDecision ? "Decision Timeline" : "Announcement history";
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== getStatusDisplay) {
    t7 = <div className="tw-flex-1 tw-text-xs tw-font-medium tw-flex tw-justify-end tw-items-center">{getStatusDisplay()}</div>;
    $[12] = getStatusDisplay;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = isOpen ? "Collapse" : "Expand";
  const t9 = `tw-w-4 tw-h-4 tw-text-iron-200 desktop-hover:group-hover:tw-text-iron-100 tw-flex-shrink-0 ${isOpen ? "tw-rotate-180" : ""} tw-transition-all tw-duration-300 tw-ease-in-out`;
  let t10;
  if ($[14] !== t9) {
    t10 = <FontAwesomeIcon icon={faChevronDown} className={t9} />;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <button className="tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-bg-iron-700/50 tw-rounded-md tw-border tw-border-solid tw-border-iron-600/40 desktop-hover:hover:tw-bg-iron-600/60 desktop-hover:hover:tw-border-iron-500/50 tw-transition-all tw-duration-300 tw-ease-out tw-flex-shrink-0" aria-label={t8}>{t10}</button>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t6 || $[21] !== t7) {
    t12 = <div className="tw-flex tw-items-center tw-gap-2 tw-w-full">{t6}{t7}{t11}</div>;
    $[19] = t11;
    $[20] = t6;
    $[21] = t7;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t3) {
    t13 = <div className="tw-@container tw-px-3 tw-py-1.5 tw-bg-iron-800/95 tw-rounded-t-lg tw-border tw-border-solid tw-border-iron-700/50 tw-cursor-pointer desktop-hover:hover:tw-bg-iron-700/80 tw-transition-all tw-duration-300 tw-ease-out tw-group tw-shadow-sm" onClick={t3}>{t12}</div>;
    $[23] = t12;
    $[24] = t3;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
};